import { nanoid } from 'nanoid';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  HStack,
  Text,
  Td,
  Tr,
  Select,
  IconButton,
  Flex,
  CloseIcon,
  SelectComponents,
} from '@frontend/components';

import CostCategoryBadge from 'components/lib2/CostCategoryBadge';
import { CustomCategoryBadge } from 'components/lib2/CustomCategoryBadge';

import { COST_CATEGORY } from 'constants/all';

import { getProfitItemDefs } from 'modules/quotes/quoteDetailsPage/data/reducer';
import Cell from 'modules/quotes/quoteItems/components/Table/Cell';

import type { FormProfitItem, ProfitItemDef } from './types';

export interface NewProfitItemRowProps {
  onClose: () => void;
  onAdd: (profitItem: FormProfitItem) => void;
}

type Option = {
  label: string;
  value: number;
  category: string;
  customCategoryName?: string;
  customCategoryColor?: string;
};

const CustomOption = ({
  children,
  data,
  ...restProps
}: SelectComponents.OptionProps<Option, false>) => (
  <SelectComponents.Option {...restProps} data={data}>
    <HStack flex={1} justifyContent="space-between">
      <Text as="span" flex={1}>
        {children}
      </Text>
      {data.category === COST_CATEGORY.CUSTOM ? (
        <CustomCategoryBadge
          name={data.customCategoryName}
          color={data.customCategoryColor}
        />
      ) : (
        <CostCategoryBadge category={data.category} />
      )}
    </HStack>
  </SelectComponents.Option>
);

export const NewProfitItemRow = ({ onClose, onAdd }: NewProfitItemRowProps) => {
  const profitItemDefs: ProfitItemDef[] = useSelector(getProfitItemDefs);

  const options: Option[] = useMemo(
    () =>
      profitItemDefs.map(({ id, name, ...rest }) => ({
        label: name,
        value: id,
        ...rest,
      })),
    [profitItemDefs],
  );

  return (
    <Tr>
      <Td py={0.25}>
        <Flex gap={1.5} alignItems="center">
          <Select
            components={{
              Option: CustomOption,
            }}
            autoFocus
            defaultMenuIsOpen
            filterOption={(option: Option, inputValue: string) =>
              option.label.toLowerCase().indexOf(inputValue.toLowerCase()) !==
              -1
            }
            onChange={({ value, label, category }: Option) => {
              // NB: the coercion to FormProfitItem is necessary because the onAdd handler expects a FormProfitItem,
              // but we can only construct a partial here, and otherwise we'd have to undefined guard everywhere else
              onAdd({
                name: label,
                profitItemDefId: value,
                id: nanoid(),
                category,
              } as FormProfitItem);
            }}
            options={options}
            placeholder="Search for pricing item"
          />
          <IconButton flexShrink={0} aria-label="Remove Pricing Item">
            <CloseIcon onClick={onClose} />
          </IconButton>
        </Flex>
      </Td>
      <Cell as="td" display="table-cell" border="none" w="unset" />
    </Tr>
  );
};
